import { ExternalLink, ShieldAlert } from 'lucide-react';
import { META_ACCOUNT_QUALITY_URL } from '@/lib/channelHealth';
import { formatDayLabel, formatTime } from '@/lib/conversationFormat';
import type { ChannelHealth } from '@/services/conversationService';

type Props = {
  channel: ChannelHealth;
  /** Server-clock ms, for "seit heute 20:09". */
  now: number;
};

/** "heute 20:09", "gestern 20:09", "Mo, 28.09. 20:09"; null without a usable time. */
function sinceText(since: string | null | undefined, now: number): string | null {
  const day = formatDayLabel(since, now);
  const time = formatTime(since);
  if (!day || !time) return null;
  return `${day === 'Heute' || day === 'Gestern' ? day.toLowerCase() : day} ${time}`;
}

/**
 * Above the chat while Meta blocks the contact's WhatsApp number. Red and
 * loud on purpose: a blocked number means the customers hear nothing — from the
 * agent and from staff — and the Meta side can only be fixed there.
 */
export function ChannelBlockedBanner({ channel, now }: Props) {
  const reason = channel.reason?.trim();
  const since = sinceText(channel.since, now);
  const detail = [reason ? `Grund: ${reason}` : null, since ? `${reason ? 'seit' : 'Seit'} ${since}` : null]
    .filter(Boolean)
    .join(' · ');

  return (
    <div
      role="alert"
      data-testid="channel-blocked-banner"
      className="flex flex-shrink-0 flex-wrap items-start gap-x-3 gap-y-2 border-b border-destructive/30 bg-destructive/10 px-3 py-2.5 sm:px-5"
    >
      <ShieldAlert className="mt-0.5 h-4 w-4 flex-shrink-0 text-destructive-strong" aria-hidden="true" />
      <div className="min-w-0 flex-1 basis-60 text-[13px] leading-snug">
        <p className="text-foreground/90">
          <strong className="font-semibold text-destructive-strong">Diese WhatsApp-Nummer ist gerade nicht erreichbar.</strong>{' '}
          Nachrichten kommen gerade nicht an – weder vom Assistenten noch von Mitarbeitern.
        </p>
        {detail && <p className="mt-0.5 text-xs text-muted-foreground">{detail}</p>}
      </div>
      <a
        href={META_ACCOUNT_QUALITY_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex h-8 flex-shrink-0 items-center gap-1.5 rounded-lg border border-destructive/40 bg-background px-3 text-xs font-semibold text-destructive-strong transition-colors hover:bg-destructive/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-destructive focus-visible:ring-offset-2 focus-visible:ring-offset-background"
      >
        Bei Meta prüfen
        <span className="sr-only"> (öffnet in neuem Tab)</span>
        <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
      </a>
    </div>
  );
}

export default ChannelBlockedBanner;
